import t from '@jetshop/intl';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import { RangeFilterListItem } from '@jetshop/ui/Filter/FilterTypes/Range/RangeFilterFlyouts';
import Drawer, { DrawerTarget, DrawerTrigger } from '@jetshop/ui/Modal/Drawer';
import { SelectableListItem } from '@jetshop/ui/Select/Components';
import { TopListItem } from '@jetshop/ui/Select/SelectDropdown';
import SortOrderDropdown from '@jetshop/ui/SortOrder/SortOrderDropdown';
import { ReactComponent as Carrot } from '@jetshop/ui/svg/Carrot.svg';
import React, { Component, Fragment } from 'react';
import styled from 'react-emotion';
import { cx } from 'emotion';
import { ActiveFilters } from './ActiveFilters';
import SortAndFilterDrawer from './SortAndFilterDrawer';
import { ListFilters } from './ListFilters';
import { MultiListFilters } from './MultiListFilters';
import useShopId from '../../../utils/useShopId';

const ButtonWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  margin-bottom: 0.6875rem;
  svg {
    transform: rotate(-90deg);
  }
`;

const MobileButton = styled('button')`
  height: 50px;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGreen};
  cursor: pointer;
  text-align: left;
  padding: 0 1.1875rem;

  font-weight: 500;
  font-size: 0.9375rem;
  letter-spacing: 0.05em;
  font-variant: small-caps;
  text-transform: lowercase;
  color: ${({ theme }) => theme.colors.green};

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Filters = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;

  [data-flight-dropdown], > div {
    min-width: 170px;
    width: auto;
    margin: 0 0.375rem 0.75rem;

    button[data-flight-dropdown-button], > button {
      height: 30px;
      border-radius: 0;
      border-color: ${({ theme }) => theme.colors.lightGrey};
      text-transform: lowercase;
      letter-spacing: 0.05em;
      font-variant: small-caps;
      font-size: 0.9375rem;
      font-weight: 500;
      font-family: ${({ theme }) => theme.fonts.primary};
      padding: 0 10px 0 13px;
      justify-content: space-between;
      color: ${({ theme }) => theme.colors.almostBlack};
      h4 {
        font-weight: inherit;
        margin: 0;
        padding: 0;
        font-size: inherit;
        color: inherit;
      }
      :focus {
        border-color: ${({ theme }) => theme.colors.lightGrey};
      }
      svg {
        margin-right: 0;
        margin-left: 0.5rem;
      }
    }

    ul[data-flight-dropdown-items], > div > div {
      border-color: ${({ theme }) => theme.colors.lightGrey};
      border-radius: 0;

      li[data-flight-dropdown-item], > button{
        border-color: ${({ theme }) => theme.colors.lightGrey};
        padding: 0 10px 0 13px;
        height: 30px; 

        :hover {
          background-color: ${({ theme }) => theme.colors.beige};
        }

        > span > span, > span:first-child {
          color: ${({ theme }) => theme.colors.almostBlack};
          font-weight: 500;
          font-size: 0.875rem;
          letter-spacing: 0.01em;
          text-transform: capitalize;
          margin-left: 0;
        }

        > span:last-child {
          font-weight: 500;
          font-size: 0.75rem;
          color: ${({ theme }) => theme.colors.grey};
        }

        svg {display: none;}
      }

    }

    ${TopListItem}, ${SelectableListItem}, ${RangeFilterListItem} {
      background-color: ${({ theme }) => theme.colors.white};
    }
  }

  &.sandstroms > .sortBy {
    min-width: 230px;
  }
`;

const FiltersSectionWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  margin: 0 -0.375rem 0.4375rem;
`;

const FiltersWapper = styled('div')`
  width: 100%;
`;

const Filter = ({ filters = [], sortOrders = [] }) => {
  const isSortOrders = sortOrders.length !== 0;
  const isFilters = filters.length !== 0;

  if (!isSortOrders && !isFilters) return null;

  const isEztrad = useShopId() === 'eztrad';

  return (
    <>
      <Above
        breakpoint="md"
        render={() => (
          <Fragment>
            <FiltersSectionWrapper>
              <FiltersWapper>
                <Filters
                  className={cx('filters-list', isEztrad ? '' : 'sandstroms')}
                >
                  <ListFilters filters={filters} />
                  <MultiListFilters filters={filters} />
                  {sortOrders.length > 0 && (
                    <SortOrderDropdown sortOrders={sortOrders} />
                  )}
                </Filters>
              </FiltersWapper>
            </FiltersSectionWrapper>
            <ActiveFilters filters={filters} />
          </Fragment>
        )}
      />
      <Below
        breakpoint="md"
        render={() => {
          return (
            <Fragment>
              <ButtonWrapper>
                <DrawerTrigger id="sortAndFilterDrawer">
                  {({ showTarget }) => (
                    <MobileButton
                      onClick={showTarget}
                      className="mobile-filter-button"
                    >
                      <span>
                        {isSortOrders && isFilters
                          ? t('Sort & Filter')
                          : isSortOrders
                          ? 'Sort'
                          : 'Filter'}
                      </span>
                      <Carrot />
                    </MobileButton>
                  )}
                </DrawerTrigger>
              </ButtonWrapper>

              <ActiveFilters filters={filters} />

              <DrawerTarget id="sortAndFilterDrawer">
                {({ hideTarget, isOpen }) => (
                  <Drawer isOpen={isOpen} right>
                    <SortAndFilterDrawer
                      close={hideTarget}
                      filters={filters}
                      sortOrders={sortOrders}
                    />
                  </Drawer>
                )}
              </DrawerTarget>
            </Fragment>
          );
        }}
      />
    </>
  );
};

export default Filter;
